<template>
	<div
		:class="['min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950', { '!bg-white dark:!bg-gray-900': hideLayout }]">
		<!-- Sidebar -->
		<Sidebar v-if="!hideLayout" v-model:isOpen="isSidebarOpen" :isCollapsed="isSidebarCollapsed" />

		<div :class="['transition-all duration-300', hideLayout ? '' : (isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64')]">
			<!-- Header -->
			<header v-if="!hideLayout"
				class="sticky top-0 z-40 flex h-20 flex-shrink-0 items-center bg-white dark:bg-gray-800 border-b border-gray-200/50 dark:border-gray-700/50 shadow-soft">
				<div class="flex items-center gap-2 px-2">
					<!-- Mobile Menu Button -->
					<button type="button"
						class="px-4 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 lg:hidden"
						@click="toggleSidebar">
						<Menu class="h-6 w-6" />
					</button>

					<!-- Desktop Collapse Button -->
					<button type="button" class="hidden h-10 w-10 items-center justify-center rounded-full border border-gray-200/70 bg-white/80 text-gray-600 shadow-sm
							transition hover:text-primary hover:shadow-md dark:border-gray-700/60 dark:bg-gray-900/60 dark:text-gray-300
							dark:hover:text-primary-dark lg:inline-flex"
						:title="isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'" @click="toggleSidebarCollapse">
						<component :is="isSidebarCollapsed ? Maximize2 : Menu" class="h-4 w-4" />
					</button>
				</div>

				<!-- Header Content -->
				<div class="flex flex-1 justify-between px-4">
					<div class="flex flex-1">
						<!-- Add your header content here -->
					</div>
					<!-- User Profile Section -->
					<div class="ml-auto flex items-center md:ml-6">
						<div class="relative ml-3">
							<!-- Profile Button -->
							<div>
								<button type="button"
								class="flex max-w-xs items-center rounded-full bg-gradient-to-br from-primary to-primary-hover dark:from-primary-dark dark:to-primary-dark-hover text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-primary-dark/50 focus:ring-offset-2 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
								@click="toggleProfile">
									<span class="sr-only">Open user menu</span>
									<div
										class="h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold ring-2 ring-white/30 dark:ring-white/20 overflow-hidden"
										:class="currentUser?.profile_picture ? '' : 'bg-white/20 dark:bg-white/10 backdrop-blur-sm'">
										<img v-if="currentUser?.profile_picture" :src="currentUser.profile_picture" alt="" class="h-full w-full object-cover" />
										<span v-else>{{ userInitials }}</span>
									</div>
								</button>
							</div>

							<!-- Profile Dropdown Menu -->
							<div v-if="isProfileOpen"
								class="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-xl bg-white dark:bg-gray-800 py-2 shadow-elegant-lg ring-1 ring-gray-200/50 dark:ring-gray-700/50 focus:outline-none"
								role="menu">
								<!-- User Info -->
								<div class="px-4 py-3 text-sm">
									<p class="font-semibold text-gray-900 dark:text-gray-100 truncate" :title="displayName">
										{{ displayName }} <span v-if="currentUser?.position" class="text-xs text-gray-500 dark:text-gray-400 font-normal">({{ currentUser.position }})</span>
									</p>
									<p class="text-gray-500 dark:text-gray-400 truncate mt-0.5" :title="currentUser?.email">
										{{ currentUser?.email }}
									</p>
								</div>
								<div class="border-t border-gray-200/50 dark:border-gray-700/50 my-1"></div>

								<!-- Profile Button -->
								<button v-if="isEmployee" type="button"
								class="block w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus:bg-gray-100/80 dark:focus:bg-gray-700/50 transition-colors duration-200 rounded-lg mx-1"
								@click="goToProfile">
									<div class="flex items-center">
										<User class="mr-3 h-5 w-5 text-gray-400 flex-shrink-0"/>
										<span class="truncate font-medium">Profile</span>
									</div>
								</button>

								<!-- Install App Button -->
								<button v-if="isInstallable" type="button"
								class="block w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus:bg-gray-100/80 dark:focus:bg-gray-700/50 transition-colors duration-200 rounded-lg mx-1"
								@click="promptInstall">
									<div class="flex items-center">
										<svg class="mr-3 h-5 w-5 text-gray-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none"
										viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
										<path stroke-linecap="round" stroke-linejoin="round"
											d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
										</svg>
										<span class="truncate font-medium text-primary dark:text-primary-dark">Install App</span>
									</div>
								</button>

								<!-- Theme Toggle -->
								<button type="button"
								class="block w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus:bg-gray-100/80 dark:focus:bg-gray-700/50 transition-colors duration-200 rounded-lg mx-1"
								@click="toggleTheme">
									<div class="flex items-center">
										<span class="mr-3 text-lg flex-shrink-0 text-gray-400">
											<SunIcon v-if="isDarkMode" />
											<MoonIcon v-else class="rotate-180" />
										</span>
										<span class="truncate font-medium">{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
									</div>
								</button>

								<!-- Logout Button -->
								<button type="button"
								class="block w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:bg-red-50 dark:focus:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 rounded-lg mx-1"
								@click="handleLogout" :disabled="isLoading">
									<div class="flex items-center">
										<LogOut class="mr-3 h-5 w-5 text-gray-400 flex-shrink-0"/>
										<span class="truncate">{{ isLoading ? 'Logging out...' : 'Sign out' }}</span>
									</div>
								</button>
							</div>
						</div>
					</div>
				</div>
			</header>

			<!-- Main Content -->
			<main :class="!hideLayout ? 'pt-8 sm:pt-10' : ''">
				<transition name="fade">
					<div class="mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
						<router-view></router-view>
					</div>
				</transition>
			</main>
		</div>
	</div>
</template>

<script setup lang="ts">
// Imports
import { ref, computed, watch, onMounted, onUnmounted, provide, } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useClickOutside } from '@/composables/ui/useClickOutside'
import { usePWAInstall } from '@/composables/ui/usePWAInstall'
import { useLogin } from '@/composables/auth/useLogin'
import { positionService } from '@/services/positionService'
import Sidebar from './Sidebar.vue'
import { Maximize2, Menu, LogOut, User } from 'lucide-vue-next'
import SunIcon from '@/components/icons/SunIcon.vue'
import MoonIcon from '@/components/icons/MoonIcon.vue'
import { useRoute } from 'vue-router'

const route = useRoute()

const hideLayout = computed(
  () => route.name === 'view-online-form' || route.name === 'profile' || route.name === 'employee-view-online-form'
)

// Store Instances
const authStore = useAuthStore()
const themeStore = useThemeStore()
const router = useRouter()
// const { handleLogout } = useLogin()

// const role = ref('')
const email = ref('')
const password = ref('')
const rememberMe = ref(false)

const { handleLogout } = useLogin({ email, password, rememberMe })
const { isInstallable, promptInstall } = usePWAInstall()

// State
const isProfileOpen = ref(false)
const isSidebarOpen = ref(false)
const isSidebarCollapsed = ref(false)
// const dismissedInstall = ref(false)

// const dismissInstall = () => {
//   	dismissedInstall.value = true
// }

// Computed Properties
const currentUser = computed(() => authStore.currentUser)

const displayName = computed(() => {
  const u = currentUser.value
  if (!u) return ''
  return u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email?.split('@')[0] || ''
})

const isLoading = computed(() => authStore.isLoading)
const isDarkMode = computed(() => themeStore.isDarkMode)
const userInitials = computed(() => {
  const u = currentUser.value
  if (!u) return '?'

  const first = u.first_name?.trim() || ''
  const last = u.last_name?.trim() || ''
  if (first && last) return (first.charAt(0) + last.charAt(0)).toUpperCase()
  if (first) return first.charAt(0).toUpperCase()

  const name = u.name?.trim()
  if (name) {
    const parts = name.split(' ').filter(Boolean)
    if (parts.length >= 2) return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    return parts[0].charAt(0).toUpperCase()
  }

  const email = u.email?.trim()
  if (email) return email.charAt(0).toUpperCase()

  return '?'
})

const isEmployee = computed(() => {
  	return currentUser.value?.role?.toLowerCase() === 'employee'
})


// Methods
const toggleProfile = () => {
  	isProfileOpen.value = !isProfileOpen.value
}

const toggleSidebar = () => {
  	isSidebarOpen.value = !isSidebarOpen.value
}

const toggleTheme = () => {
  	themeStore.toggleTheme()
}

const toggleSidebarCollapse = () => {
  	isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const goToProfile = () => {
	if (router.hasRoute('profile')) {
		router.push({ name: 'profile' })
	}
	isProfileOpen.value = false
}

// Lifecycle Hooks
const { handleClickOutside } = useClickOutside(isProfileOpen)

const resolvePosition = async () => {
	const user = authStore.currentUser
	if (!user || !user.position_id || user.position) return
	try {
		const positions = await positionService.getPositions()
		const matched = positions.find(p => p.id === user.position_id)
		
		if (matched) {
			user.position = matched.name
			const storage = sessionStorage.getItem('token') ? sessionStorage : localStorage
			if (storage.getItem('token')) {
				storage.setItem('user', JSON.stringify({ ...user }))
			}

			return user.position
		}
	} catch (err) {
		console.warn('Failed to resolve position:', err)
    	return user?.position
	}
}

watch(
	() => authStore.currentUser,
	async (user) => {
		if (!user) return
		await resolvePosition()
	},
	{ immediate: true }
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  	document.removeEventListener('click', handleClickOutside)
})

provide('isSidebarCollapsed', isSidebarCollapsed)
provide('toggleSidebarCollapse', toggleSidebarCollapse)
</script>
